import React from 'react';
import { CheckSquare } from 'lucide-react';
import { TYPES } from './SheetFields';
import { TONES, SheetIcon } from './sheetStyle';
import FishboneDiagram from './FishboneDiagram';
import FocusTimer, { minutesFrom } from './FocusTimer';

// Sheet rendered to mirror the printed worksheet: rows of coloured boxes side by side (stack on phones).
const SPAN = { 3: 'md:col-span-3', 4: 'md:col-span-4', 5: 'md:col-span-5', 6: 'md:col-span-6', 7: 'md:col-span-7', 8: 'md:col-span-8', 9: 'md:col-span-9', 12: 'md:col-span-12' };
const COLS = { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3' };

const Header = ({ f, tone }) => (
  <div className={`mb-3 ${f.center ? 'text-center' : ''}`}>
    <div className={`flex items-center gap-2 ${f.center ? 'justify-center' : ''}`}>
      {f.num && <span className="w-9 h-9 min-w-[2.25rem] rounded-full text-white font-heading text-lg flex items-center justify-center" style={{ background: tone.ink }}>{f.num}</span>}
      {f.headIcon && <SheetIcon name={f.headIcon} color={tone.ink} size={34} />}
      <span className="font-body text-sm md:text-base font-bold uppercase tracking-[0.1em] leading-tight px-3 py-1 rounded-2xl" style={{ color: '#5a2f50', background: tone.pill }}>{f.label}</span>
    </div>
    {f.hint && <p className="font-body text-[15px] text-stone-600 mt-2 italic">{f.hint}</p>}
  </div>
);

const Quote = ({ f, joined }) => {
  const tone = TONES[f.tone || 'plum'];
  if (f.banner) {
    return (
      <div className="rounded-3xl px-6 py-5 flex items-center gap-5" style={{ background: tone.bg }} data-testid={`fb-cell-${f.id}`}>
        <SheetIcon name={f.icon || 'Lightbulb'} color="#5a2f50" size={44} />
        <div className="flex-1 border-l border-[#5a2f50]/40 pl-5">
          {f.label && <p className="font-body text-lg font-bold uppercase tracking-[0.12em] text-[#5a2f50] mb-1">{f.label}</p>}
          {f.lines.map((l) => <p key={l} className={`text-[#5a2f50] ${f.label ? 'font-body text-[15px]' : 'font-heading italic text-xl md:text-2xl text-center'}`}>{l}</p>)}
        </div>
      </div>
    );
  }
  return (
    <div className={`h-full ${joined ? '' : 'rounded-3xl p-5'}`} style={joined ? {} : { background: tone.bg }} data-testid={`fb-cell-${f.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        {f.label && <span className="font-heading italic text-3xl text-[#5a2f50]">{f.label}</span>}
        <SheetIcon name={f.icon} color={tone.ink} />
      </div>
      <ul className="space-y-2">{f.lines.map((l) => <li key={l} className="font-body text-[15px] text-stone-700 flex gap-2"><span>•</span>{l}</li>)}</ul>
    </div>
  );
};

const Cell = ({ f, data, onChange, joined, readOnly }) => {
  if (f.type === 'quote') return <Quote f={f} joined={joined} />;
  const tone = TONES[f.tone || 'plum'];
  const Field = TYPES[f.type];
  const set = (val) => onChange({ ...data, [f.id]: val });
  if (f.bare) return <div data-testid={`fb-cell-${f.id}`}><Field f={f} v={data[f.id]} set={set} /></div>;
  return (
    <div className={`relative h-full flex flex-col ${joined ? '' : 'rounded-3xl p-5'}`} style={joined ? {} : { background: tone.bg }} data-testid={`fb-cell-${f.id}`}>
      {f.icon && !f.center && <span className="absolute right-5 top-5 opacity-90"><SheetIcon name={f.icon} color={tone.ink} size={36} /></span>}
      <div className={f.icon && !f.center ? 'pr-12' : ''}><Header f={f} tone={tone} /></div>
      <div className={`flex-1 ${f.inner === false ? '' : 'bg-white/90 rounded-2xl p-4'} ${f.icon && f.center ? 'pb-14 relative' : ''}`}>
        <Field f={{ ...f, label: null, hint: null, starHint: null }} v={data[f.id]} set={set} />
        {f.timer && !readOnly && <FocusTimer minutes={minutesFrom(data[f.id])} testId={`fb-timer-${f.id}`} />}
        {f.icon && f.center && <span className="absolute right-4 bottom-4"><SheetIcon name={f.icon} color={tone.ink} /></span>}
      </div>
    </div>
  );
};

const NextStep = ({ f, data, onChange }) => (
  <div className="rounded-3xl p-5 md:p-6 flex items-center gap-5" style={{ background: TONES.green.bg }} data-testid="fb-cell-next">
    <CheckSquare size={48} strokeWidth={1.6} className="text-[#5a2f50] flex-shrink-0 hidden sm:block" />
    <div className="flex-1 sm:border-l sm:border-[#5a2f50]/40 sm:pl-5">
      <p className="font-body text-lg font-bold uppercase tracking-[0.12em] text-[#5a2f50]">{f.label && f.label !== 'My next tiny step' ? f.label : 'My next tiny step'}</p>
      <p className="font-body text-[15px] text-stone-700 mb-1">{f.hint}</p>
      <TYPES.text f={{ ...f, label: null, hint: null }} v={data.next} set={(val) => onChange({ ...data, next: val })} />
    </div>
  </div>
);

const Slot = ({ item, byId, ...rest }) => (Array.isArray(item)
  ? <div className="flex flex-col gap-5 h-full">{item.map((id) => <Cell key={id} f={byId[id]} {...rest} />)}</div>
  : <Cell f={byId[item]} {...rest} />);

export default function SheetLayout({ sheet, data, onChange, readOnly = false }) {
  const byId = Object.fromEntries(sheet.fields.map((f) => [f.id, f]));
  return (
    <div className="space-y-5" data-testid={`fb-layout-${sheet.id}`}>
      {sheet.id === 'fishbone' && <FishboneDiagram data={data} />}
      {sheet.layout.map((row, ri) => {
        if (row.cols.length === 1 && row.cols[0] === 'next') return <NextStep key={ri} f={byId.next} data={data} onChange={onChange} />;
        const grid = row.widths ? 'grid grid-cols-1 md:grid-cols-12 gap-5' : `grid grid-cols-1 ${COLS[row.cols.length]} gap-5`;
        const slots = row.cols.map((item, ci) => (
          <div key={ci} className={row.widths ? SPAN[row.widths[ci]] : ''}>
            <Slot item={item} byId={byId} data={data} onChange={onChange} joined={row.joined} readOnly={readOnly} />
          </div>
        ));
        return row.joined
          ? <div key={ri} className={`${grid} rounded-3xl border-2 border-[#d9c6d4] bg-white p-5 md:divide-x md:divide-[#d9c6d4] [&>*:not(:first-child)]:md:pl-6`}>{slots}</div>
          : <div key={ri} className={grid}>{slots}</div>;
      })}
    </div>
  );
}
